import { debounce } from 'lodash';
import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { ContextualMenu, InputSearch } from 'sonata-react-components';

import { isSharedReportSession } from '@/helpers/shareReport/shareReport.helper';
import { useFootfallReportSocket } from '@/hooks/useFootfallReportSocket';
import { getFootfallStoreDetailFile } from '@/services/footfall.service';
import { useGlobalStore } from '@/store/global';
import { getSession } from '@/store/global/session/selectors';

import type { StoreDetailFileFormat } from '@/services/footfall.service';

import './storeDetailTable.scss';

import { PoiStoreTable } from '@/components/driveToStore/common/PoiStoreTable/PoiStoreTable';

type Props = {
  entityType: string;
  entityId: string;
  country?: string;
  userId: string;
  venueIds?: string[];
};

type State = {
  generatingFormat: StoreDetailFileFormat | null;
  nameFilter: string;
};

export default function StoreDetailTable({ entityType, entityId, country, userId, venueIds }: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const isShared = isSharedReportSession(useGlobalStore(getSession));
  const listId = 'storeDetailTable';

  const [{ nameFilter, generatingFormat }, setState] = useState<State>({
    generatingFormat: null,
    nameFilter: ''
  });

  const handleFileReady = (urlFile: string, fileFormat: string) => {
    setState(prevState => ({ ...prevState, generatingFormat: null }));
    const extension = fileFormat === 'csv' ? 'csv' : 'xlsx';
    fetch(urlFile)
      .then(res => res.blob())
      .then(blob => {
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = `store_detail.${extension}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      })
      .catch(() => {
        window.open(urlFile, '_blank');
      });
  };

  const handleFileError = () => {
    setState(prevState => ({ ...prevState, generatingFormat: null }));
  };

  useFootfallReportSocket({ entityId, onFileError: handleFileError, onFileReady: handleFileReady });

  const handleDownload = async (fileFormat: StoreDetailFileFormat) => {
    if (generatingFormat !== null) {
      return;
    }
    setState(prevState => ({ ...prevState, generatingFormat: fileFormat }));
    try {
      await getFootfallStoreDetailFile(entityType, entityId, userId, {
        country,
        fileFormat,
        nameFilter: nameFilter || undefined,
        venueIds
      });
    } catch {
      setState(prevState => ({ ...prevState, generatingFormat: null }));
    }
  };

  const handleSearchChange = useMemo(
    () =>
      debounce((value: string) => {
        setState(prevState => ({ ...prevState, nameFilter: value.trim() }));
      }, 400),
    []
  );

  return (
    <div className="storeDetailTable">
      <div className="storeDetailTable__header">
        <span className="storeDetailTable__icon fa-duotone fa-solid fa-store" />
        <h3 className="storeDetailTable__title">
          {formatMessage({
            defaultMessage: 'Store Detail',
            id: 'driveToStore.aggregatedReport.storeDetail.title'
          })}
        </h3>
        <div className="storeDetailTable__headerRight">
          {!isShared && (
            <ContextualMenu
              buttonConfig={{
                icon: 'fa-regular fa-arrow-down-to-line',
                loading: generatingFormat !== null,
                shape: 'squareRounded',
                size: 'ultraSmall',
                text: formatMessage({
                  defaultMessage: 'Download list',
                  id: 'driveToStore.aggregatedReport.storeDetail.downloadList'
                }),
                type: 'secondary'
              }}
              id={'storeDetailTable-download'}
            >
              <ContextualMenu.Item
                icon="far fa-file-csv"
                id={'storeDetailTable-download-csv'}
                loading={generatingFormat === 'csv'}
                onClick={() => handleDownload('csv')}
                text={formatMessage({
                  defaultMessage: 'Download .CSV',
                  id: 'driveToStore.aggregatedReport.storeDetail.downloadCsv'
                })}
              />
              <ContextualMenu.Item
                icon="far fa-file-excel"
                id={'storeDetailTable-download-excel'}
                loading={generatingFormat === 'excel'}
                onClick={() => handleDownload('excel')}
                text={formatMessage({
                  defaultMessage: 'Download Excel',
                  id: 'driveToStore.aggregatedReport.storeDetail.downloadExcel'
                })}
              />
            </ContextualMenu>
          )}
          <div className="storeDetailTable__search">
            <InputSearch
              id={'storeDetailTable__search'}
              lens
              name="storeDetailTableSearch"
              onChange={handleSearchChange}
              placeholder={formatMessage({
                defaultMessage: 'Search by store name or address',
                id: 'driveToStore.aggregatedReport.storeDetail.searchPlaceholder'
              })}
              tiny
            />
          </div>
        </div>
      </div>
      <PoiStoreTable
        fetchParams={{
          country,
          entityId,
          entityType,
          nameFilter,
          userId,
          venueIds
        }}
        id={listId}
        showBestDay={true}
      />
    </div>
  );
}
